import React, { FocusEvent, ForwardedRef, forwardRef, KeyboardEvent, MouseEvent, PropsWithChildren, useMemo } from 'react';

type FlexProps = {
    fullWidth?: boolean;
    style?: React.CSSProperties;
    column?: boolean;
    role?: string;
    row?: boolean;
    reverse?: boolean;
    id?: string;
    wrap?: boolean;
    inline?: boolean;
    justify?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly' | string;
    align?: 'center' | 'flex-start' | 'flex-end' | 'stretch' | string;
    center?: boolean;
    className?: string;
    onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
    onContextMenu?: (e?: MouseEvent) => void;
    onFocus?: (e?: FocusEvent<HTMLDivElement>) => void;
    onBlur?: (e?: FocusEvent<HTMLDivElement>) => void;
    onKeyDown?: (e?: KeyboardEvent) => void;
    onMouseEnter?: (e?: MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (e?: MouseEvent<HTMLDivElement>) => void;
    tabIndex?: number;
    testId?: string;
};
const FlexComponent = (props: PropsWithChildren<FlexProps>, ref: ForwardedRef<HTMLDivElement>) => {
    const flexDirection: 'row' | 'row-reverse' | 'column' | 'column-reverse' = useMemo(() => {
        const direction = props.column ? 'column' : 'row';
        return props.reverse ? (`${direction}-reverse` as 'row-reverse' | 'column-reverse') : direction;
    }, [props.column, props.reverse]);

    return (
        <div
            className={props.className}
            data-testid={props.testId}
            id={props.id}
            ref={ref}
            style={{
                width: props.fullWidth ? '100%' : undefined,
                ...props.style,
                display: props.style?.display || props.inline ? 'inline-flex' : 'flex',
                cursor: props.onClick ? 'pointer' : undefined,
                flexDirection,
                justifyContent: (props.center ? 'center' : undefined) || props.justify || undefined,
                flexWrap: props.wrap ? 'wrap' : 'nowrap',
                alignItems: (props.center ? 'center' : undefined) || props.align || undefined
            }}
            onBlur={props.onBlur}
            onClick={props.onClick}
            onContextMenu={props.onContextMenu}
            onKeyDown={(e) => (props.onKeyDown ? props.onKeyDown(e) : e.key === 'Enter' && props.onClick && props.onClick())}
            onFocus={props.onFocus}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            role={props?.role || 'presentation'}
            tabIndex={typeof props.tabIndex === 'number' ? props.tabIndex : props.role === 'button' ? 0 : undefined}
        >
            {props.children}
        </div>
    );
};

export const Flex = forwardRef<HTMLDivElement, PropsWithChildren<FlexProps>>(FlexComponent);
